export type Role =
  | "owner"
  | "manager"
  | "instructor"
  | "desk"
  | "accounting"
  | "read_only";
export type AttendanceStatus =
  | "unmarked"
  | "present"
  | "absent"
  | "late"
  | "left_early";
export type ExceptionStatus =
  | "open"
  | "proposed"
  | "awaiting_confirmation"
  | "resolved"
  | "deferred"
  | "cancelled";
export type ExceptionKind =
  | "security"
  | "attendance"
  | "makeup"
  | "guardian"
  | "billing"
  | "payroll"
  | "support";
export type ReceiptState =
  | "draft"
  | "pending"
  | "accepted"
  | "delivered"
  | "failed"
  | "not_applicable";

export interface TenantSummary {
  id: string;
  name: string;
  timezone: string;
  businessDayCutoff: string;
  mode: "sample" | "workspace";
}

export interface Student {
  id: string;
  displayName: string;
  schoolGrade: string;
  guardianLabel: string;
  guardianContactMasked: string;
  active: boolean;
  billingPlan: "monthly" | "session";
  remainingSessions: number | null;
  classIds: string[];
}

export interface TeamMember {
  id: string;
  displayName: string;
  role: Role;
  scopeClassIds: string[];
  state: "active" | "invitation_prepared" | "disabled";
  invitationReceipt: ReceiptState;
  createdAt: string;
  updatedAt: string;
}

export interface ImportReceipt {
  id: string;
  fileName: string;
  acceptedRows: number;
  rejectedRows: number;
  duplicateRows: number;
  insertedStudentIds: string[];
  state: "committed" | "rolled_back";
  createdAt: string;
  rolledBackAt: string | null;
}

export interface DataRequest {
  id: string;
  kind: "deletion";
  state: "received" | "cancelled" | "completed";
  requestedAt: string;
  scheduledFor: string;
  completedAt: string | null;
  legalHoldNotice: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  actorId: string;
  targetIds: string[];
  summary: string;
  occurredAt: string;
}

export interface ClassGroup {
  id: string;
  name: string;
  subject: string;
  instructorId: string;
  instructorName: string;
  room: string;
  capacity: number;
  weekdays: number[];
  startTime: string;
  endTime: string;
  compensationKrw: number;
  studentIds: string[];
}

export interface ClassSession {
  id: string;
  classId: string;
  startsAt: string;
  endsAt: string;
  state: "scheduled" | "in_progress" | "completed" | "cancelled";
  revision: number;
}

export interface AttendanceRecord {
  id: string;
  sessionId: string;
  studentId: string;
  status: AttendanceStatus;
  recordedAt: string | null;
  source: "server" | "offline_queue" | "import";
  syncState: "idle" | "pending" | "synced" | "conflict";
  version: number;
  note?: string;
}

export interface SessionLedgerEntry {
  id: string;
  studentId: string;
  sessionId: string;
  kind: "consume" | "restore" | "correction" | "monthly_attendance";
  delta: number;
  balanceAfter: number | null;
  reason: string;
  occurredAt: string;
  reversesEntryId?: string;
}

export interface MakeupCandidate {
  id: string;
  studentId: string;
  sourceAttendanceId: string;
  eligibility: "eligible" | "ineligible" | "needs_review";
  reason: string;
  suggestedSessionIds: string[];
  bookedSessionId: string | null;
  expiresAt: string;
  revision: number;
}

export interface GuardianDraft {
  id: string;
  studentId: string;
  purpose: "attendance" | "makeup" | "billing" | "learning";
  body: string;
  recipientMasked: string;
  receiptState: ReceiptState;
  requiresReview: boolean;
  quietHoursBlocked: boolean;
  capabilityId: string | null;
  updatedAt: string;
}

export interface LearningNote {
  id: string;
  sessionId: string;
  studentId: string | null;
  progress: string;
  homework: string;
  visibility: "internal" | "guardian";
  revision: number;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  studentId: string;
  period: string;
  amountKrw: number;
  discountKrw: number;
  paidKrw: number;
  refundedKrw: number;
  balanceKrw: number;
  state: "draft" | "issued" | "partial" | "paid" | "overpaid" | "refunded";
  dueAt: string;
}

export interface PaymentReceipt {
  id: string;
  invoiceId: string;
  kind: "payment" | "refund" | "reversal";
  amountKrw: number;
  source: "manual" | "provider_disabled";
  occurredAt: string;
  reversedById?: string;
  reversesReceiptId?: string;
}

export interface CompensationCandidate {
  id: string;
  instructorId: string;
  sessionId: string;
  ruleVersion: string;
  amountKrw: number;
  reason: string;
  state: "candidate" | "approved" | "excluded";
  approvedAt: string | null;
}

export interface ExceptionCase {
  id: string;
  kind: ExceptionKind;
  title: string;
  summary: string;
  studentId: string | null;
  sourceEventId: string;
  status: ExceptionStatus;
  severity: "critical" | "high" | "normal" | "low";
  cause: string;
  rule: string;
  impact: string;
  suggestedAction: string;
  dueAt: string;
  deferReason?: string;
  resolvedAt?: string;
  revision: number;
}

export interface EffectReceipt {
  id: string;
  effectType: "message" | "payment" | "export" | "support" | "close" | "undo";
  subjectId: string;
  state: ReceiptState;
  provider: "product" | "disabled";
  summary: string;
  occurredAt: string;
}

export interface CloseSnapshot {
  id: string;
  period: "day" | "week" | "month" | "term";
  periodKey: string;
  revision: number;
  completedAt: string;
  reopenedAt: string | null;
  reopenReason: string | null;
  counts: {
    sessions: number;
    students: number;
    messages: number;
    makeups: number;
    billingDifferences: number;
    deferred: number;
  };
  unresolvedIds: string[];
}

export interface SupportCase {
  id: string;
  category:
    | "how_to"
    | "bug"
    | "billing"
    | "privacy"
    | "security"
    | "data_loss"
    | "child"
    | "access"
    | "legal";
  severity: "P0" | "P1" | "P2" | "P3";
  subject: string;
  redactedBody: string;
  route: string;
  requestId: string;
  state:
    | "received"
    | "pending_delivery"
    | "human_required"
    | "answer_drafted"
    | "resolved";
  providerState: ReceiptState;
  createdAt: string;
  updatedAt: string;
  clusterKey: string;
}

export interface OperatingSignal {
  id: string;
  title: string;
  state: "observed" | "unknown" | "healthy";
  numerator: number;
  denominator: number;
  period: string;
  explanation: string;
  sourceIds: string[];
  staleAt: string;
}

export interface WorkspaceState {
  schemaVersion: 1;
  tenant: TenantSummary;
  version: number;
  generatedAt: string;
  students: Student[];
  teamMembers: TeamMember[];
  classes: ClassGroup[];
  sessions: ClassSession[];
  attendance: AttendanceRecord[];
  ledger: SessionLedgerEntry[];
  makeups: MakeupCandidate[];
  guardianDrafts: GuardianDraft[];
  learningNotes: LearningNote[];
  invoices: Invoice[];
  paymentReceipts: PaymentReceipt[];
  compensation: CompensationCandidate[];
  exceptions: ExceptionCase[];
  effectReceipts: EffectReceipt[];
  closeSnapshots: CloseSnapshot[];
  supportCases: SupportCase[];
  importReceipts: ImportReceipt[];
  dataRequests: DataRequest[];
  auditLog: AuditEntry[];
  signals: OperatingSignal[];
  trial: {
    startsAt: string;
    endsAt: string;
    plan: "trial" | "solo" | "team" | "studio";
    providerState: "disabled" | "active" | "past_due";
    firstRealCloseAt: string | null;
    exportAlwaysAvailable: true;
  };
  preferences: {
    reducedMotion: boolean;
    highContrast: boolean;
    messageQuietHours: { start: string; end: string };
  };
}

export type WorkspaceCommand =
  | {
      type: "attendance.mark";
      sessionId: string;
      studentId: string;
      status: AttendanceStatus;
      note?: string;
    }
  | { type: "attendance.bulkPresent"; sessionId: string }
  | {
      type: "exception.resolve";
      exceptionId: string;
      action: "accept_suggestion" | "manual_done";
    }
  | { type: "exception.defer"; exceptionId: string; reason: string }
  | { type: "makeup.book"; makeupId: string; sessionId: string }
  | { type: "makeup.cancel"; makeupId: string; reason: string }
  | {
      type: "learning.save";
      sessionId: string;
      studentId?: string;
      progress: string;
      homework: string;
      visibility: "internal" | "guardian";
    }
  | {
      type: "billing.payment";
      invoiceId: string;
      amountKrw: number;
      kind: "payment" | "refund";
    }
  | {
      type: "billing.invoiceIssue";
      studentId: string;
      period: string;
      amountKrw: number;
      discountKrw: number;
      dueAt: string;
    }
  | { type: "billing.paymentReverse"; receiptId: string; reason: string }
  | { type: "payroll.approve"; candidateId: string }
  | {
      type: "close.complete";
      period: "day" | "week" | "month" | "term";
      periodKey: string;
    }
  | { type: "close.reopen"; snapshotId: string; reason: string }
  | {
      type: "support.create";
      category: SupportCase["category"];
      subject: string;
      body: string;
      route: string;
      requestId: string;
    }
  | { type: "support.resolve"; caseId: string; note: string }
  | {
      type: "student.add";
      displayName: string;
      schoolGrade: string;
      guardianContactMasked: string;
      billingPlan: Student["billingPlan"];
      remainingSessions?: number;
    }
  | {
      type: "class.create";
      name: string;
      subject: string;
      instructorId: string;
      instructorName: string;
      room: string;
      capacity: number;
      weekdays: number[];
      startTime: string;
      endTime: string;
      compensationKrw: number;
    }
  | { type: "class.rosterUpdate"; classId: string; studentIds: string[] }
  | {
      type: "team.invitationPrepare";
      displayName: string;
      role: Role;
      scopeClassIds: string[];
    }
  | {
      type: "team.roleChange";
      memberId: string;
      role: Role;
      scopeClassIds: string[];
    }
  | {
      type: "import.students";
      fileName: string;
      rows: Array<{
        displayName: string;
        schoolGrade: string;
        guardianContactMasked: string;
        billingPlan: Student["billingPlan"];
        remainingSessions?: number;
      }>;
      rejectedRows: number;
    }
  | { type: "import.rollback"; receiptId: string }
  | { type: "guardian.draftApprove"; draftId: string }
  | { type: "guardian.capabilityAttach"; draftId: string; capabilityId: string }
  | { type: "data.deletionRequest"; confirmation: string }
  | { type: "data.deletionCancel"; requestId: string }
  | { type: "trial.planIntent"; plan: "solo" | "team" | "studio" }
  | {
      type: "preferences.update";
      reducedMotion?: boolean;
      highContrast?: boolean;
    };

export interface CommandContext {
  now: string;
  actorId: string;
  actorRole: Role | "sample_user";
  scopeClassIds: string[] | null;
  eventId: string;
  idempotencyKey: string;
}

export interface CommandReceipt {
  eventId: string;
  eventType: WorkspaceCommand["type"];
  version: number;
  changedIds: string[];
  nextActions: string[];
  summary: string;
  occurredAt: string;
}

export class DomainError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly retryable = false,
    public readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = "DomainError";
  }
}
